/**
 * Structure - Base class for buildable structures
 */

import { Vector3 } from '../math/Vector3';
import { Renderer } from '../core/Renderer';
import { StructureType } from '../types/enums';
import { InventoryComponent } from '../items/InventoryComponent';
import { hasStructureSprite, renderStructureSprite } from './StructureSprites';

let nextStructureId = 1;

/**
 * CampfireData - Tracks campfire state
 */
export interface CampfireData {
  fuel: number;           // Wood fuel remaining (in seconds of burn time)
  maxFuel: number;        // Max fuel capacity
  isBurning: boolean;     // Whether fire is lit
  cookingItem: string | null;  // Item type being cooked
  rawQuantity: number;         // How many raw items waiting to cook
  cookingProgress: number;     // 0-1 progress on current item
  cookedQuantity: number;      // How many are done and ready to collect
  cookingTime: number;         // Time to cook one item (seconds)
}

export interface Blueprint {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly structureType: StructureType;
  readonly size: { width: number; height: number };
  readonly materials: { itemId: string; quantity: number }[];
  readonly health: number;
  readonly color: string;
  readonly requiresFloor?: boolean; // If true, can only be placed on floor structures
}

export class Structure {
  readonly id: string;
  readonly blueprint: Blueprint;
  position: Vector3;
  health: number;
  maxHealth: number;

  // Storage for chests
  storage: InventoryComponent | null = null;

  // Campfire data
  campfire: CampfireData | null = null;

  // Animation
  private animationTime: number = 0;

  constructor(blueprint: Blueprint, position: Vector3) {
    this.id = `structure_${nextStructureId++}`;
    this.blueprint = blueprint;
    this.position = position.clone();
    this.health = blueprint.health;
    this.maxHealth = blueprint.health;

    // Initialize storage for storage structures
    if (blueprint.structureType === StructureType.Storage) {
      this.storage = new InventoryComponent(20);
    }

    // Initialize campfire data for furnace structures
    if (blueprint.structureType === StructureType.Furnace) {
      this.campfire = {
        fuel: 0,
        maxFuel: 300, // 5 minutes max fuel
        isBurning: false,
        cookingItem: null,
        rawQuantity: 0,
        cookingProgress: 0,
        cookedQuantity: 0,
        cookingTime: 10 // 10 seconds to cook one item
      };
    }
  }

  update(deltaTime: number): void {
    this.animationTime += deltaTime;

    // Update campfire
    if (this.campfire && this.campfire.isBurning) {
      // Consume fuel
      this.campfire.fuel -= deltaTime;
      if (this.campfire.fuel <= 0) {
        this.campfire.fuel = 0;
        this.campfire.isBurning = false;
      }

      // Cook items one at a time
      if (this.campfire.cookingItem && this.campfire.rawQuantity > 0 && this.campfire.isBurning) {
        this.campfire.cookingProgress += deltaTime / this.campfire.cookingTime;
        if (this.campfire.cookingProgress >= 1) {
          // One item finished cooking - move to cooked pile
          this.campfire.rawQuantity--;
          this.campfire.cookedQuantity++;

          // Start cooking next item or clear if done
          if (this.campfire.rawQuantity > 0) {
            this.campfire.cookingProgress = 0;
          } else {
            // All raw items cooked
            this.campfire.cookingProgress = 0;
            if (this.campfire.cookedQuantity === 0) {
              this.campfire.cookingItem = null;
            }
          }
        }
      }
    }
  }

  /** Add fuel to campfire (returns amount actually added) */
  addFuel(amount: number): number {
    if (!this.campfire) return 0;
    const fuelPerWood = 30; // 30 seconds per wood
    const fuelToAdd = amount * fuelPerWood;
    const spaceAvailable = this.campfire.maxFuel - this.campfire.fuel;
    const actualFuel = Math.min(fuelToAdd, spaceAvailable);
    this.campfire.fuel += actualFuel;

    // Auto-ignite if fuel added
    if (this.campfire.fuel > 0) {
      this.campfire.isBurning = true;
    }

    return Math.ceil(actualFuel / fuelPerWood);
  }

  /** Add items to cook (can add more to existing batch if same item type) */
  startCooking(itemId: string, quantity: number = 1): number {
    if (!this.campfire) return 0;
    if (!this.campfire.isBurning) return 0; // Need fire

    // If already cooking different item, can't add
    if (this.campfire.cookingItem && this.campfire.cookingItem !== itemId) return 0;

    // Add to raw queue (doesn't reset progress on current item)
    if (this.campfire.cookingItem === itemId) {
      this.campfire.rawQuantity += quantity;
      return quantity;
    }

    // Start new cooking batch
    this.campfire.cookingItem = itemId;
    this.campfire.rawQuantity = quantity;
    this.campfire.cookingProgress = 0;
    this.campfire.cookedQuantity = 0;
    return quantity;
  }

  /** Collect cooked items (returns { itemId, quantity } or null) */
  collectCookedItem(): { itemId: string; quantity: number } | null {
    if (!this.campfire) return null;
    if (this.campfire.cookedQuantity <= 0) return null;
    if (!this.campfire.cookingItem) return null;

    // Map raw items to cooked items
    const cookedMap: { [key: string]: string } = {
      'raw_meat': 'cooked_meat'
    };

    const cookedItemId = cookedMap[this.campfire.cookingItem] ?? null;
    if (!cookedItemId) return null;

    const quantity = this.campfire.cookedQuantity;
    this.campfire.cookedQuantity = 0;

    // Clear cooking item if nothing left to cook
    if (this.campfire.rawQuantity === 0) {
      this.campfire.cookingItem = null;
      this.campfire.cookingProgress = 0;
    }

    return { itemId: cookedItemId, quantity };
  }

  /** Check if campfire is burning */
  isBurning(): boolean {
    return this.campfire?.isBurning ?? false;
  }

  /** Get light radius (for rendering) */
  getLightRadius(): number {
    if (!this.campfire || !this.campfire.isBurning) return 0;
    // Flickering effect
    const flicker = Math.sin(this.animationTime * 8) * 0.1 + 1;
    return 4 * flicker; // 4 tile radius
  }

  render(renderer: Renderer): void {
    const structType = this.blueprint.structureType;
    const ctx = renderer.getContext();

    // Check if this structure has a pixel art sprite
    if (hasStructureSprite(this.blueprint.id)) {
      // Draw shadow
      const shadowPos = renderer.worldToScreen(new Vector3(this.position.x, this.position.y, 0));
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
      ctx.beginPath();
      ctx.ellipse(shadowPos.x, shadowPos.y + 5, 20 * renderer.getZoom(), 10 * renderer.getZoom(), 0, 0, Math.PI * 2);
      ctx.fill();

      // Calculate sprite position and size
      const spriteHeight = structType === StructureType.Furnace ? 6 : 12;
      const screenPos = renderer.worldToScreen(new Vector3(this.position.x, this.position.y, spriteHeight));
      const spriteSize = 50 * renderer.getZoom();

      // Render the pixel art sprite
      renderStructureSprite(
        ctx,
        this.blueprint.id,
        screenPos.x - spriteSize / 2,
        screenPos.y - spriteSize / 2,
        spriteSize
      );

      // Render fire effect on top for campfire if burning
      if (structType === StructureType.Furnace && this.campfire?.isBurning) {
        this.renderFireEffect(renderer);
      }
    } else {
      // Fallback to block rendering for structures without sprites
      if (structType === StructureType.Floor) {
        // Render as thin block so it's visible above grass
        const height = 3;
        const topColor = this.blueprint.color;
        const leftColor = this.darkenColor(topColor, 0.7);
        const rightColor = this.darkenColor(topColor, 0.5);
        renderer.drawIsometricBlock(this.position, height, topColor, leftColor, rightColor);
      } else if (structType === StructureType.Door) {
        // Render as shorter block with gap
        const height = 20;
        const topColor = this.blueprint.color;
        const leftColor = this.darkenColor(topColor, 0.7);
        const rightColor = this.darkenColor(topColor, 0.5);
        renderer.drawIsometricBlock(this.position, height, topColor, leftColor, rightColor);
      } else if (structType === StructureType.Furnace) {
        // Campfire fallback - render stone base
        const height = 6;
        renderer.drawIsometricBlock(this.position, height, '#555', '#444', '#333');

        // Render fire effect if burning
        if (this.campfire?.isBurning) {
          this.renderFireEffect(renderer);
        }
      } else {
        // Standard block rendering
        const height = structType === StructureType.Wall ? 24 : 16;
        const topColor = this.blueprint.color;
        const leftColor = this.darkenColor(topColor, 0.7);
        const rightColor = this.darkenColor(topColor, 0.5);
        renderer.drawIsometricBlock(this.position, height, topColor, leftColor, rightColor);
      }
    }

    // Health bar if damaged
    if (this.health < this.maxHealth) {
      const height = structType === StructureType.Floor ? 10 : (structType === StructureType.Wall ? 24 : 16);
      renderer.drawHealthBar(
        new Vector3(this.position.x, this.position.y, height + 10),
        this.health,
        this.maxHealth,
        30,
        4
      );
    }
  }

  /** Render fire flames effect */
  private renderFireEffect(renderer: Renderer): void {
    const ctx = renderer.getContext();
    const basePos = renderer.worldToScreen(new Vector3(this.position.x, this.position.y, 8));

    // Animated flame particles
    const numFlames = 5;
    for (let i = 0; i < numFlames; i++) {
      const phase = this.animationTime * 4 + i * 1.2;
      const flameHeight = 12 + Math.sin(phase) * 4;
      const offsetX = Math.sin(phase * 0.7 + i) * 4;

      // Flame gradient colors
      const innerColor = `rgba(255, 200, 50, ${0.8 - Math.sin(phase) * 0.2})`;
      const outerColor = `rgba(255, 100, 0, ${0.6 - Math.sin(phase) * 0.2})`;

      // Draw flame
      ctx.beginPath();
      ctx.moveTo(basePos.x + offsetX - 4, basePos.y);
      ctx.quadraticCurveTo(
        basePos.x + offsetX,
        basePos.y - flameHeight,
        basePos.x + offsetX + 4,
        basePos.y
      );
      ctx.fillStyle = outerColor;
      ctx.fill();

      // Inner flame
      ctx.beginPath();
      ctx.moveTo(basePos.x + offsetX - 2, basePos.y);
      ctx.quadraticCurveTo(
        basePos.x + offsetX,
        basePos.y - flameHeight * 0.7,
        basePos.x + offsetX + 2,
        basePos.y
      );
      ctx.fillStyle = innerColor;
      ctx.fill();
    }

    // Glow effect
    const gradient = ctx.createRadialGradient(basePos.x, basePos.y - 8, 0, basePos.x, basePos.y - 8, 30);
    gradient.addColorStop(0, 'rgba(255, 150, 50, 0.3)');
    gradient.addColorStop(1, 'rgba(255, 100, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(basePos.x, basePos.y - 8, 30, 0, Math.PI * 2);
    ctx.fill();
  }

  /** Check if this structure blocks movement for a given entity type */
  blocksMovement(isPlayer: boolean): boolean {
    const structType = this.blueprint.structureType;

    // Floors are walkable by player only
    if (structType === StructureType.Floor) {
      return !isPlayer;
    }

    // Doors are passable by player only
    if (structType === StructureType.Door) {
      return !isPlayer;
    }

    // Walls always block
    if (structType === StructureType.Wall) {
      return true;
    }

    // Other structures (chests, workbenches) block movement
    return true;
  }

  /** Check if this structure is interactable */
  isInteractable(): boolean {
    return this.blueprint.structureType === StructureType.Storage ||
           this.blueprint.structureType === StructureType.CraftingStation ||
           this.blueprint.structureType === StructureType.Furnace ||
           this.blueprint.structureType === StructureType.HellHole ||
           this.blueprint.structureType === StructureType.Ladder;
  }

  takeDamage(amount: number): void {
    this.health = Math.max(0, this.health - amount);
  }

  repair(amount: number): void {
    this.health = Math.min(this.maxHealth, this.health + amount);
  }

  isDestroyed(): boolean {
    return this.health <= 0;
  }

  getOccupiedTiles(): Vector3[] {
    const tiles: Vector3[] = [];
    for (let dx = 0; dx < this.blueprint.size.width; dx++) {
      for (let dy = 0; dy < this.blueprint.size.height; dy++) {
        tiles.push(new Vector3(
          Math.floor(this.position.x) + dx,
          Math.floor(this.position.y) + dy,
          0
        ));
      }
    }
    return tiles;
  }

  private darkenColor(hex: string, factor: number): string {
    // Handle rgb format
    if (hex.startsWith('rgb')) {
      const match = hex.match(/\d+/g);
      if (match && match.length >= 3) {
        const r = Number(match[0]) || 0;
        const g = Number(match[1]) || 0;
        const b = Number(match[2]) || 0;
        return `rgb(${Math.floor(r * factor)}, ${Math.floor(g * factor)}, ${Math.floor(b * factor)})`;
      }
    }

    // Handle hex format
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgb(${Math.floor(r * factor)}, ${Math.floor(g * factor)}, ${Math.floor(b * factor)})`;
  }
}

// Default blueprints
export const BLUEPRINTS: Blueprint[] = [
  // {
  //   id: 'wall_wood',
  //   name: 'Wooden Wall',
  //   description: 'A basic wooden wall.',
  //   structureType: StructureType.Wall,
  //   size: { width: 1, height: 1 },
  //   materials: [{ itemId: 'wood', quantity: 5 }],
  //   health: 100,
  //   color: '#8b6914'
  // },
  // {
  //   id: 'wall_stone',
  //   name: 'Stone Wall',
  //   description: 'A sturdy stone wall.',
  //   structureType: StructureType.Wall,
  //   size: { width: 1, height: 1 },
  //   materials: [{ itemId: 'stone', quantity: 10 }],
  //   health: 250,
  //   color: '#707070'
  // },
  // {
  //   id: 'door_wood',
  //   name: 'Wooden Door',
  //   description: 'Player can pass, enemies cannot.',
  //   structureType: StructureType.Door,
  //   size: { width: 1, height: 1 },
  //   materials: [{ itemId: 'wood', quantity: 8 }],
  //   health: 75,
  //   color: '#8b4513'
  // },
  {
    id: 'floor_wood',
    name: 'Wood Floor',
    description: 'Walkable by player only.',
    structureType: StructureType.Floor,
    size: { width: 1, height: 1 },
    materials: [{ itemId: 'wood', quantity: 8 }],
    health: 50,
    color: '#deb887'
  },
  {
    id: 'chest_wood',
    name: 'Wooden Chest',
    description: 'Stores 20 items. Must be placed on floor.',
    structureType: StructureType.Storage,
    size: { width: 1, height: 1 },
    materials: [{ itemId: 'wood', quantity: 10 }],
    health: 50,
    color: '#a0522d',
    requiresFloor: true
  },
  {
    id: 'workbench',
    name: 'Workbench',
    description: 'Craft advanced items. Click to use.',
    structureType: StructureType.CraftingStation,
    size: { width: 1, height: 1 },
    materials: [
      { itemId: 'wood', quantity: 100 },
      { itemId: 'stone', quantity: 100 }
    ],
    health: 200,
    color: '#cd853f'
  },
  {
    id: 'campfire',
    name: 'Campfire',
    description: 'Provides light and cooks food. Needs wood fuel.',
    structureType: StructureType.Furnace,
    size: { width: 1, height: 1 },
    materials: [
      { itemId: 'wood', quantity: 10 },
      { itemId: 'stone', quantity: 5 }
    ],
    health: 50,
    color: '#ff6b35'
  },
  {
    id: 'hell_hole',
    name: 'Hell Hole',
    description: 'A dark portal to the underworld. Click to enter.',
    structureType: StructureType.HellHole,
    size: { width: 1, height: 1 },
    materials: [], // Created by jackdrill, not built manually
    health: 9999,  // Indestructible
    color: '#1a0a0a'
  },
  {
    id: 'ladder',
    name: 'Escape Ladder',
    description: 'Climb back to the surface world.',
    structureType: StructureType.Ladder,
    size: { width: 1, height: 1 },
    materials: [{ itemId: 'wood', quantity: 10 }],
    health: 100,
    color: '#8b6914'
  }
];
